/**
 * Activation API service.
 *
 * Centralizes all activation code-related API calls with consistent error handling
 * and TypeScript types.
 *
 * @module activationApi
 */

import { apiDelete, apiGet, apiPost } from './apiUtils';

/**
 * Activation code interface
 */
export interface ActivationCode {
  id: string;
  code: string;
  is_used: boolean;
  created_at?: string;
  used_at?: string;
  used_by_username?: string;
}

/**
 * Activation codes summary
 */
export interface ActivationSummary {
  total: number;
  used: number;
  unused: number;
}

/**
 * List activation codes response
 */
export interface ListActivationCodesResponse {
  codes: ActivationCode[];
  summary: ActivationSummary;
}

/**
 * Generate activation codes request
 */
export interface GenerateCodesRequest {
  count: number;
}

/**
 * Generate activation codes response
 */
export interface GenerateCodesResponse {
  codes: string[];
  count: number;
}

/**
 * Fetch all activation codes with summary
 *
 * @returns {Promise<ListActivationCodesResponse>} List of codes and summary
 * @throws {Error} If API request fails
 */
export const getActivationCodes = async (): Promise<ListActivationCodesResponse> => {
  const result = await apiGet<ListActivationCodesResponse>('/admin/activation-codes', {
    errorMessage: 'Failed to fetch activation codes',
    context: 'getActivationCodes',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to fetch activation codes');
  }
  return result;
};

/**
 * Generate new activation codes
 *
 * @param {number} count - Number of codes to generate (1-100)
 * @returns {Promise<GenerateCodesResponse>} Generated codes
 * @throws {Error} If API request fails
 */
export const generateActivationCodes = async (
  count: number,
): Promise<GenerateCodesResponse> => {
  if (Number.isNaN(count) || count < 1 || count > 100) {
    throw new Error('Count must be between 1 and 100');
  }

  const result = await apiPost<GenerateCodesResponse>(
    '/admin/activation-codes/generate',
    { count },
    {
      errorMessage: 'Failed to generate activation codes',
      context: 'generateActivationCodes',
      throwOnError: true,
    },
  );
  if (!result) {
    throw new Error('Failed to generate activation codes');
  }
  return result;
};

/**
 * Delete an activation code by ID
 *
 * @param {string} id - Activation code ID to delete
 * @returns {Promise<void>} Resolves on successful deletion
 * @throws {Error} If API request fails
 */
export const deleteActivationCode = async (id: string): Promise<void> => {
  await apiDelete(`/admin/activation-codes/${id}`, {
    errorMessage: 'Failed to delete activation code',
    context: 'deleteActivationCode',
    throwOnError: true,
  });
};
